import type { ReactNode } from 'react'
import styles from './ProfileMenu.module.css'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { useHotkey } from '@/hooks/keyboard-hooks'
import { Link } from '@/components/Link'
import { isEmptyNode } from '@/utils/react-util'
import { externalLinks, links } from '@/utils/link'

export interface ProfileMenuProps {
  visible: boolean

  onRequestClose(): unknown

  avatar: AvatarSubject
  displayName: string
  email: string
  shouldShowBilling: boolean
  canEnrollInMultiFactorAuth: boolean
  onEnrollInMultiFactorAuth(): unknown
  onAddNewWorkspace(): unknown
  isFirmAdmin: boolean
  isPro: boolean
}

export function ProfileMenu({
  onRequestClose,
  visible,
  avatar,
  displayName,
  email,
  shouldShowBilling,
  canEnrollInMultiFactorAuth,
  onEnrollInMultiFactorAuth,
  onAddNewWorkspace,
  isFirmAdmin,
  isPro,
}: ProfileMenuProps) {
  useHotkey(['Escape'], onRequestClose)

  return (
    <div className={visible ? styles.visible : styles.hidden}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- click-outside backdrop; keyboard users dismiss with Esc via useHotkey */}
      <div className={styles.overlay} onClick={onRequestClose} />
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- delegated handler that closes the menu after a menu item's own action runs */}
      <div className={styles.box} onClick={onRequestClose}>
        <Section>
          <div className={styles.profile}>
            <div className={styles.avatarContainer}>
              <Avatar size={32} subject={avatar} />
            </div>
            <div className={styles.profileContent}>
              <p className={styles.profileTitle}>{displayName}</p>
              <p className={styles.profileSubtitle}>{email}</p>
            </div>
          </div>
        </Section>
        <Section>
          <SectionTitle>Account</SectionTitle>
          <SectionItem
            to={links.profileGeneral()}
            testId="profile-general-page-button"
          >
            Profile
          </SectionItem>
          {shouldShowBilling && (
            <SectionItem
              to={links.profileBilling()}
              testId="profile-billing-page-button"
            >
              Billing
            </SectionItem>
          )}
          <SectionItem
            to={links.profileNotifications()}
            testId="profile-notifications-page-button"
          >
            Notification settings
          </SectionItem>
          {canEnrollInMultiFactorAuth && (
            <SectionItem onPress={onEnrollInMultiFactorAuth}>
              Enroll in multi-factor authentication
            </SectionItem>
          )}
          {isPro && (
            <SectionItem
              href={externalLinks.proSupportTicket()}
              testId="profile-pro-support-button"
            >
              Pro support
            </SectionItem>
          )}
        </Section>

        <Section>
          {!isFirmAdmin && (
            <SectionItem onPress={onAddNewWorkspace}>
              Add new workspace
            </SectionItem>
          )}
          <SectionItem to={links.logout()}>Log out</SectionItem>
        </Section>
      </div>
    </div>
  )
}

interface SectionTitleProps {
  children?: ReactNode
}

function SectionTitle({ children }: SectionTitleProps) {
  return <p className={styles.sectionTitle}>{children}</p>
}

interface SectionProps {
  children?: ReactNode
}

function Section({ children }: SectionProps) {
  if (isEmptyNode(children)) {
    return null
  }
  return <div className={styles.section}>{children}</div>
}

interface SectionItemProps {
  children?: ReactNode
  to?: string
  href?: string

  onPress?(): void

  /**
   * Test selector.
   */
  testId?: string
}

function SectionItem({
  children,
  href,
  to,
  testId,
  onPress,
}: SectionItemProps) {
  return (
    <Link
      to={to}
      href={href}
      className={styles.sectionItem}
      target={href ? '_blank' : undefined}
      onClick={() => onPress?.()}
      data-test={testId}
    >
      <div className={styles.sectionItemText}>{children}</div>
    </Link>
  )
}
